import { forwardRef, type ButtonHTMLAttributes } from "react"
import { Button as UiButton } from "@/components/ui/button"
import { MARKV_TRACE_SIZE, MarkVTraceLoader } from "@/components/compass/MarkVTraceLoader"
import { cn } from "@/lib/utils"

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger"
export type ButtonSize = "sm" | "md" | "lg"

const VARIANT_MAP: Record<ButtonVariant, "default" | "secondary" | "ghost" | "destructive"> = {
  primary: "default",
  secondary: "secondary",
  ghost: "ghost",
  danger: "destructive",
}

// Cor aplicada direto em markv-* (mesmo padrão de Badge/Alert). Não dependemos
// das utilitárias semânticas do shadcn (bg-primary/bg-destructive) porque o
// Tailwind só tem markv-* registrado: sem isso o fundo some e sobra só o texto
// (bug do botão branco no branco). Branco (text-markv-text-50) só sobre fundo
// de cor; ghost/secondary usam tinta escura.
//
// O `!` (important) é necessário: o primitivo ui/button injeta as classes
// shadcn (bg-destructive/bg-primary) de mesma especificidade, e sem important
// quem vencia dependia da ordem no CSS gerado — o fundo base perdia e o botão
// só ficava colorido no hover. Com important o estado normal já vem colorido.
const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "bg-markv-primary-700! text-markv-text-50! hover:bg-markv-primary-800!",
  secondary: "bg-markv-surface-200! text-markv-text-700! hover:bg-markv-surface-300!",
  ghost: "bg-transparent! text-markv-text-700! hover:bg-markv-surface-200!",
  danger: "bg-markv-danger-600! text-markv-text-50! hover:bg-markv-danger-700!",
}

// Variantes que pintam fundo de cor forte e usam tinta clara (text-markv-text-50).
// Nelas o loader precisa inverter junto: o gradiente verde da marca sobre o verde
// do primary some, e sobre o vermelho do danger fica errado.
const VARIANT_INK_CLARA: Record<ButtonVariant, boolean> = {
  primary: true,
  secondary: false,
  ghost: false,
  danger: true,
}

const SIZE_MAP: Record<ButtonSize, "sm" | "default" | "lg"> = {
  sm: "sm",
  md: "default",
  lg: "lg",
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", loading = false, className, disabled, children, type, ...rest },
  ref,
) {
  return (
    <UiButton
      ref={ref}
      type={type ?? "button"}
      data-variant={variant}
      data-size={size}
      variant={VARIANT_MAP[variant]}
      size={SIZE_MAP[size]}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cn(VARIANT_CLASS[variant], className)}
      {...rest}
    >
      {/* label vazio de propósito: o texto do botão já anuncia a ação, então o
          loader entra decorativo para não virar "Carregando Salvando" no leitor
          de tela. O entalhe do Traço é recorte real, então ele funciona sobre
          o fundo de qualquer variante sem precisar de cor por contexto. */}
      {loading && (
        <MarkVTraceLoader
          size={MARKV_TRACE_SIZE.button}
          label=""
          inverse={VARIANT_INK_CLARA[variant]}
        />
      )}
      {children}
    </UiButton>
  )
})
